import { ButtonElement } from "appworks/graphics/elements/button-element";
import { SmartShape } from "appworks/graphics/elements/smart-shape";
import { Layers } from "appworks/graphics/layers/layers";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { Services } from "appworks/services/services";
import { CoverControl } from "appworks/ui/controls/standard/shape/cover-control";
import { GameNameControlFactory } from "appworks/ui/factories/autoplay/game-name-control-factory";
import { TimeControlFactory } from "appworks/ui/factories/autoplay/time-control-factory";
import { ControlFactory } from "appworks/ui/factories/control-factory";
import { GenericControlFactory } from "appworks/ui/factories/generic-control-factory";
import { GameJuriInfoControlFactory } from "appworks/ui/factories/standard/game-juri-info-control-factory";
import { SkipAreaControlFactory } from "appworks/ui/factories/standard/skip-area-control-factory";
import { SoundToggleControlFactory } from "appworks/ui/factories/standard/sound-toggle-control-factory";
import { TotalStakeTextControlFactory } from "appworks/ui/factories/standard/total-stake-text-control-factory";
import { TurboControlFactory } from "appworks/ui/factories/standard/turbo-control-factory";
import { UIButtonControlFactory } from "appworks/ui/factories/standard/ui-button-control-factory";
import { UIToggleControlFactory } from "appworks/ui/factories/standard/ui-toggle-control-factory";
import { UIFlagState } from "appworks/ui/flags/ui-flag-state";
import { UIFlag, UIMenuFlag } from "appworks/ui/flags/ui-flags";
import { UI, UIScene } from "appworks/ui/ui";
import { deviceInfo } from "appworks/utils/device-info";
import { GetEpicAutoplayFactories } from "epic/controls/factories/get-epic-autoplay-factories";
import { GetEpicStandardFactories } from "epic/controls/factories/get-epic-standard-factories";
import { gamevyModel } from "gamevy/gamevy-model";
import Logger = require("js-logger");
import { PartnerConnectManager } from "partner-connect/partner-connect-manager";
import { PartnerConnectHistoryButtonControl } from "partner-connect/ui/controls/partner-connect-history-button-control";
import { PartnerConnectHomeButtonControl } from "partner-connect/ui/controls/partner-connect-home-button-control";
import { SpinControlFactory } from "partner-connect/ui/factories/partner-connect-spin-control-factory";
import { PartnerConnectTotalStakeStepperControlFactory } from "partner-connect/ui/factories/partner-connect-total-stake-stepper-control-factory";
import { slotModel } from "slotworks/model/slot-model";
import { Jurisdiction } from "slotworks/services/jurisdiction/jurisdiction-service";
import { ygg } from "ygg/ygg";
import { TGRHiddenDisplayObjectControl } from "./factories/controls/tgr-hidden-display-object-control";
import { TGRToggleDisabledControlFactory } from "./factories/tgr-toggle-disabled-control-factory";

export function TGRSetupUI(...factories: ControlFactory[]) {
    setupSpin();
    setupTurbo();
    setupSkip();
    setupBetBar();
    setupAutoplay();
    setupRules();
    setupMenus();
    setupPaytable();
    setupHeader();
    setupGermany();

    const initControlFactories = [
        new SpinControlFactory(),
        new SkipAreaControlFactory(),
        new PartnerConnectTotalStakeStepperControlFactory(),
        new TotalStakeTextControlFactory(),
        new SoundToggleControlFactory(),
        new UIButtonControlFactory(),
        new UIToggleControlFactory(),
        new TurboControlFactory(),
        new TimeControlFactory(),
        new GameNameControlFactory(),
        new GameJuriInfoControlFactory(),
        new GenericControlFactory("cover", SmartShape, CoverControl),
        ...GetEpicAutoplayFactories(),
        ...GetEpicStandardFactories(),
        new GenericControlFactory("history", ButtonElement, PartnerConnectHistoryButtonControl)
    ];

    const deInitControlFactoryTypes = [];

    const customParameters = PartnerConnectManager.getConfig().customParameters;

    if (customParameters.disableMenuHome === "yes") {
        initControlFactories.push(new GenericControlFactory("home", ButtonElement, TGRHiddenDisplayObjectControl));
    } else {
        initControlFactories.push(new GenericControlFactory("home", ButtonElement, PartnerConnectHomeButtonControl));
    }
    if (customParameters.disableMenuPaytable === "yes") {
        initControlFactories.push(new TGRToggleDisabledControlFactory("BetBar", "ui_paytable"));
    }
    const inferredSettingsDisable = customParameters.disableMenuHome === "yes" && customParameters.disableMenuHistory === "yes" && customParameters.disableMenuRules === "yes";
    if (customParameters.disableMenuSettings === "yes" || inferredSettingsDisable) {
        initControlFactories.push(new TGRToggleDisabledControlFactory("BetBar", "ui_settings"));
    }
    if (customParameters.disableMenuAudio === "yes") {
        Services.sound.unmute();
        Services.sound.setMusicMute(false);
        Services.sound.setSoundFxMute(false);
        deInitControlFactoryTypes.push(SoundToggleControlFactory);
        initControlFactories.push(new GenericControlFactory("sound_toggle_on", ButtonElement, TGRHiddenDisplayObjectControl));
        initControlFactories.push(new GenericControlFactory("sound_toggle_off", ButtonElement, TGRHiddenDisplayObjectControl));
        initControlFactories.push(new GenericControlFactory("sound_toggle_loading", Sprite, TGRHiddenDisplayObjectControl));
    }
    if (customParameters.disableMenuRules === "yes" || customParameters.license === "fin") {
        document.getElementById("rules").innerHTML = "";
    }
    if (PartnerConnectManager.isHistoryMode()) {
        Services.sound.unmute();
        Services.sound.setMusicMute(false);
        Services.sound.setSoundFxMute(false);
    }

    filterControlFactories(initControlFactories, deInitControlFactoryTypes);
    UI.init(initControlFactories);
}

function filterControlFactories<T extends ControlFactory>(ontrolFactories: T[], unwantedControlFactoryTypes: Array<{ new(...args: any[]): T }>): T[] {
    ontrolFactories.forEach((controlFactory: T, i: number) => {
        unwantedControlFactoryTypes.forEach((deInitType: { new(...args: any[]): T }) => {
            if (controlFactory instanceof deInitType) {
                ontrolFactories.splice(i, 1);
            }
        });
    });
    return ontrolFactories;
}

function setupSpin() {
    const smartButtonUIScenes: UIScene[] = [
        { scene: "minimal", show: new UIFlagState(UIFlag.BONUS) },
        { scene: "freespins", show: UIFlagState.FREESPINS },
        { scene: "auto", show: new UIFlagState(UIFlag.AUTO_SPIN), enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN) }
    ];

    let spinButtonFlagState: UIFlagState;

    if (deviceInfo.isDesktop) {
        spinButtonFlagState = new UIFlagState(UIFlag.ALWAYS);
    } else {
        spinButtonFlagState = new UIFlagState(UIFlag.ALWAYS, UIMenuFlag.PAYTABLE);
    }

    smartButtonUIScenes.push({ scene: "spin", show: spinButtonFlagState, enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN) });

    UI.layer(Layers.get("SmartButton"), smartButtonUIScenes);
}

function setupTurbo() {
    const turboAllowed = slotModel.read().regulations.turboAllowed;
    if (!turboAllowed) {
        return;
    }

    const turboUIScenes: UIScene[] = [
        { scene: "minimal", show: new UIFlagState(UIFlag.BONUS) },
        { scene: "freespins", show: UIFlagState.FREESPINS },
        { scene: "auto", show: new UIFlagState(UIFlag.AUTO_SPIN), enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN) }
    ];

    let turboFlagState: UIFlagState;

    if (deviceInfo.isDesktop) {
        turboFlagState = new UIFlagState(UIFlag.ALWAYS);
    } else {
        turboFlagState = new UIFlagState(UIFlag.ALWAYS, UIMenuFlag.PAYTABLE);
    }

    turboUIScenes.push({ scene: "spin", show: turboFlagState, enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN) });

    UI.layer(Layers.get("Turbo"), turboUIScenes);
}

function setupSkip() {
    const skipAllowed = slotModel.read().regulations.skipAllowed;
    if (!skipAllowed) {
        return;
    }

    UI.layer(Layers.get("Skip"), [{
        scene: "skip",
        show: new UIFlagState(UIFlag.GAME_IN_PROGRESS, UIFlag.IDLE),
        enable: new UIFlagState(UIFlag.ALWAYS, UIFlag.NO_SKIP | UIFlag.MIN_SPIN_COOLDOWN)
    }]);
}

function setupBetBar() {
    UI.layer(Layers.get("BetBar"), [
        { scene: "minimal", show: new UIFlagState(UIFlag.BONUS) },
        { scene: "freespins", show: UIFlagState.FREESPINS, enable: UIFlagState.NEVER },
        { scene: "base", show: UIFlagState.ALWAYS, enable: UIFlagState.IDLE }
    ]);
}

function setupAutoplay() {
    const juriAllowsAutoplay = Services.jurisdiction.getAutoplayEnabled();
    const licenseAllowsAutoplay: boolean = ygg.licensesAutoplayDisabled.indexOf(PartnerConnectManager.getConfig().customParameters?.license?.toLowerCase()) === -1;
    if (!juriAllowsAutoplay || !licenseAllowsAutoplay) {
        UI.layer(Layers.get("Autoplay"), [
            { scene: "minimal", show: UIFlagState.NEVER, enable: UIFlagState.NEVER },
            { scene: "auto", show: UIFlagState.NEVER, enable: UIFlagState.NEVER },
            { scene: "spin", show: UIFlagState.NEVER, enable: UIFlagState.NEVER }
        ]);
        return;
    }

    UI.toggle(Layers.get("Autoplay"), "ui_autoplay", UIMenuFlag.AUTO);
    UI.button(Layers.get("MenuContent"), "close", 0, [UIMenuFlag.AUTO]);

    let autoFlagState: UIFlagState;

    if (deviceInfo.isDesktop) {
        autoFlagState = new UIFlagState(UIFlag.ALWAYS, UIFlag.FREE_SPINS | UIFlag.BONUS);
    } else {
        autoFlagState = new UIFlagState(UIFlag.ALWAYS, UIMenuFlag.PAYTABLE | UIFlag.FREE_SPINS | UIFlag.BONUS);
    }

    UI.layer(Layers.get("Autoplay"), [
        { scene: "minimal", show: new UIFlagState(UIFlag.BONUS), enable: UIFlagState.ALWAYS },
        { scene: "auto", show: new UIFlagState(UIFlag.AUTO_SPIN, UIFlag.FREE_SPINS), enable: UIFlagState.ALWAYS },
        { scene: "spin", show: autoFlagState, enable: new UIFlagState(UIFlag.IDLE) }
    ]);
}

function setupRules() {
    UI.toggle(Layers.get("BetBar"), "ui_settings", UIMenuFlag.RULES);
    UI.button(Layers.get("MenuContent"), "close", 0, [UIMenuFlag.RULES]);
}

function setupMenus() {
    const customParameters = PartnerConnectManager.getConfig().customParameters;
    const isFinLicense: boolean = customParameters?.license?.toLowerCase() === "fin";
    const rulesDisabled: boolean = customParameters?.disableMenuRules === "yes";
    const rulesScene: string = isFinLicense || rulesDisabled ? "rules_minimal" : "rules";

    UI.layer(Layers.get("MenuContent"), [
        { scene: "autoplay", show: UIFlagState.AUTOPLAY_MENU },
        { scene: rulesScene, show: UIFlagState.RULES_MENU },
        { scene: "yggdrasil", show: UIFlagState.NEVER }
    ]);
}

function setupPaytable() {
    UI.toggle(Layers.get("BetBar"), "ui_paytable", UIMenuFlag.PAYTABLE);
    if (Layers.get("PaytableCommon")) {
        UI.button(Layers.get("PaytableCommon"), "close", 0, [UIMenuFlag.PAYTABLE]);
        UI.button(Layers.get("PaytableCommon"), "close_portrait", 0, [UIMenuFlag.PAYTABLE]);
        UI.layer(Layers.get("PaytableCommon"), [{ scene: "paytable", show: new UIFlagState(UIMenuFlag.PAYTABLE) }]);
    } else {
        Logger.error("PaytableCommon layer is missing. This should be added to the game for paytable support in the Epic UI.");
    }
}

function setupHeader() {
    UI.layer(Layers.get("Header"), [{ scene: "header", show: UIFlagState.ALWAYS }]);
}

function setupGermany() {
    if (Layers.get("Jurisdiction")) {
        const enabled = gamevyModel.jurisdiction === Jurisdiction.Germany ? UIFlag.ALWAYS : UIFlag.NEVER;
        const disabled = UIFlag.BONUS | UIFlag.FREE_SPINS | UIMenuFlag.MENU;
        UI.layer(Layers.get("Jurisdiction"), [{ scene: "base", show: new UIFlagState(enabled, disabled) }]);
    } else {
        throw new Error("Games need German jurisidictional info psd layers");
    }
}
